import { useRef, useState } from 'react';
import { useParams, NavLink } from 'react-router-dom';
import {
  useLeaderboard, useGroupedLeaderboard,
  useInviteFriend, useFriends,
  useAcceptFriend, useRemoveFriend,
} from '../lib/api';
import type { LeaderboardEntry } from '@shared/types';
import { VerifiedBadge } from '../components/VerifiedBadge';
import {
  motion, AnimatePresence, springs, Pressable, PageTransition, Modal, CountUp, Skeleton, Confetti,
} from '../lib/motion';
import { haptic } from '../lib/haptics';

const SCOPES = [
  { code: 'friends', label: 'Friends' },
  { code: 'class', label: 'Class' },
  { code: 'school', label: 'School' },
  { code: 'exam', label: 'Exam' },
  { code: 'global', label: 'Global' },
] as const;

export function LeaderboardPage() {
  const { scope } = useParams();
  const activeScope = scope ?? 'friends';

  return (
    <PageTransition className="max-w-2xl">
      <header className="mb-4">
        <h1 className="font-display text-2xl font-semibold text-squishy-dark tracking-tight">Rankings</h1>
        <p className="text-sm text-slate-500 mt-0.5">Rolling 30-day XP across your circles.</p>
      </header>

      <nav aria-label="Leaderboard scope" className="flex gap-1.5 mb-5 text-sm overflow-x-auto no-scrollbar -mx-1 px-1">
        {SCOPES.map((s) => (
          <NavLink
            key={s.code}
            to={`/leaderboard/${s.code}`}
            onClick={() => haptic.light()}
            className="relative shrink-0 flex items-center min-h-[44px] px-4 rounded-full outline-none focus-visible:ring-2 focus-visible:ring-squishy-teal"
          >
            {({ isActive }) => (
              <>
                {isActive && (
                  <motion.span
                    layoutId="scope-pill"
                    className="absolute inset-0 rounded-full bg-squishy-primary shadow-pop"
                    transition={springs.snappy}
                  />
                )}
                {!isActive && <span className="absolute inset-0 rounded-full bg-slate-100" aria-hidden="true" />}
                <span className={`relative z-10 font-semibold transition-colors ${isActive ? 'text-white' : 'text-slate-500'}`}>
                  {s.label}
                </span>
              </>
            )}
          </NavLink>
        ))}
      </nav>

      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={activeScope}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.16, ease: 'easeOut' }}
        >
          {activeScope === 'friends' && <FriendsTab />}
          {activeScope === 'class' && <GroupedTab scope="class" />}
          {activeScope === 'school' && <GroupedTab scope="school" />}
          {activeScope === 'exam' && <GroupedTab scope="exam" />}
          {activeScope === 'global' && <GlobalTab />}
        </motion.div>
      </AnimatePresence>
    </PageTransition>
  );
}

// ── Friends Tab ──────────────────────────────────────────────────────────────
function FriendsTab() {
  const { data } = useLeaderboard('friends');
  const invite = useInviteFriend();
  const friends = useFriends();
  const accept = useAcceptFriend();
  const remove = useRemoveFriend();
  const [q, setQ] = useState('');
  const [removeTarget, setRemoveTarget] = useState<LeaderboardEntry | null>(null);
  // Keep the last target around so the confirm card doesn't blank out mid-exit.
  const lastTarget = useRef<LeaderboardEntry | null>(null);
  if (removeTarget) lastTarget.current = removeTarget;
  const shownTarget = removeTarget ?? lastTarget.current;

  const incoming = (friends.data?.friends ?? []).filter(
    (f) => f.status === 'pending' && f.direction === 'incoming',
  );
  const outgoing = (friends.data?.friends ?? []).filter(
    (f) => f.status === 'pending' && f.direction === 'outgoing',
  );

  const handleInvite = async () => {
    const val = q.trim();
    if (!val) return;
    invite.reset();
    if (val.includes('@')) await invite.mutateAsync({ email: val });
    else await invite.mutateAsync({ handle: val });
    setQ('');
  };

  return (
    <div className="space-y-4">
      {/* Add friend */}
      <div className="bg-white border border-slate-200/80 rounded-2xl shadow-card p-4">
        <form onSubmit={(e) => { e.preventDefault(); handleInvite(); }} className="flex gap-2">
          <input
            value={q}
            onChange={(e) => { setQ(e.target.value); invite.reset(); }}
            placeholder="Name, email, or username"
            className="flex-1 min-w-0 rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-sm focus:outline-none focus:border-squishy-teal focus:bg-white transition-colors"
          />
          <Pressable
            type="submit"
            disabled={invite.isPending || q.trim().length < 2}
            className="bg-squishy-primary text-white px-5 min-h-[44px] rounded-xl text-sm font-semibold shadow-pop disabled:opacity-50 disabled:shadow-none"
          >Send</Pressable>
        </form>
        {invite.isSuccess && <p className="text-xs text-emerald-600 mt-2">If that user exists, they'll receive your friend invite!</p>}
        {invite.isError && <p className="text-xs text-red-500 mt-2">Something went wrong. Please try again.</p>}
      </div>

      {/* Incoming requests */}
      {incoming.length > 0 && (
        <div className="bg-white border border-squishy-teal/30 rounded-2xl shadow-card p-4">
          <h3 className="font-display text-sm font-semibold text-squishy-navy tracking-tight mb-1">
            Friend requests
            <span className="ml-2 text-[11px] font-semibold text-squishy-tealDark bg-teal-50 rounded-full px-2 py-0.5 tabular-nums align-middle">{incoming.length}</span>
          </h3>
          <ul className="divide-y divide-slate-100">
            {incoming.map((f) => (
              <li key={f.id} className="flex items-center gap-3 py-2 text-sm">
                <UserAvatar name={formatFriendName(f)} />
                <div className="flex-1 min-w-0 truncate font-medium text-squishy-dark">{formatFriendName(f)}</div>
                <Pressable
                  haptic="success"
                  onClick={() => accept.mutate(f.id)}
                  className="shrink-0 bg-squishy-primary text-white px-4 min-h-[44px] text-xs font-semibold rounded-full shadow-pop"
                >Accept</Pressable>
                <Pressable
                  onClick={() => remove.mutate(f.id)}
                  className="shrink-0 border border-slate-200 px-4 min-h-[44px] text-xs font-medium rounded-full text-slate-500 active:bg-slate-50 transition-colors"
                >Decline</Pressable>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Outgoing (sent) requests — pending until accepted */}
      {outgoing.length > 0 && (
        <div className="bg-white border border-slate-200/80 rounded-2xl shadow-card p-4">
          <h3 className="font-display text-sm font-semibold text-squishy-navy tracking-tight mb-1">
            Sent requests
            <span className="ml-2 text-[11px] font-semibold text-slate-500 bg-slate-100 rounded-full px-2 py-0.5 tabular-nums align-middle">{outgoing.length}</span>
          </h3>
          <ul className="divide-y divide-slate-100">
            {outgoing.map((f) => (
              <li key={f.id} className="flex items-center gap-3 py-2 text-sm">
                <UserAvatar name={formatFriendName(f)} />
                <div className="flex-1 font-medium min-w-0 truncate text-squishy-dark">{formatFriendName(f)}</div>
                <span className="text-[11px] font-medium uppercase tracking-wide text-slate-400 shrink-0">Pending</span>
                <Pressable
                  onClick={() => remove.mutate(f.id)}
                  disabled={remove.isPending}
                  aria-label="Cancel request"
                  className="shrink-0 p-3 -mr-2 rounded-full text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-50"
                >
                  <TrashIcon />
                </Pressable>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Leaderboard */}
      <div className="flex items-baseline justify-between pt-1">
        <h2 className="font-display text-lg font-semibold text-squishy-navy tracking-tight">Friends</h2>
        <span className="text-[11px] font-medium uppercase tracking-wide text-slate-400">Last 30 days</span>
      </div>
      <Podium entries={data?.entries ?? []} />
      <LeaderboardList entries={data?.entries ?? []} onRemove={setRemoveTarget} loading={!data} />
      {data?.currentUserRank && (
        <p className="text-xs text-slate-500 mt-2 text-center">Your rank: #{data.currentUserRank} of {data.totalUsers}</p>
      )}

      {/* Remove-friend confirmation */}
      <Modal open={Boolean(removeTarget)} onClose={() => { if (!remove.isPending) setRemoveTarget(null); }}>
        {shownTarget && (
          <div className="bg-white rounded-3xl p-6 shadow-pop">
            <h3 className="font-display text-lg font-semibold text-squishy-dark tracking-tight">Remove friend?</h3>
            <p className="text-sm text-slate-600 mt-2">
              Remove <span className="font-semibold">{shownTarget.displayName}</span> from your friends? You'll both stop appearing in each other's friends list and rankings. You can add them again later.
            </p>
            <div className="flex gap-2 mt-5">
              <Pressable
                onClick={() => setRemoveTarget(null)}
                disabled={remove.isPending}
                className="flex-1 border border-slate-200 rounded-xl min-h-[44px] text-sm font-medium text-slate-600 active:bg-slate-50 transition-colors disabled:opacity-50"
              >Cancel</Pressable>
              <Pressable
                haptic="heavy"
                onClick={async () => {
                  try { await remove.mutateAsync(shownTarget.userId); } finally { setRemoveTarget(null); }
                }}
                disabled={remove.isPending}
                className="flex-1 bg-red-500 text-white rounded-xl min-h-[44px] text-sm font-semibold disabled:opacity-60"
              >{remove.isPending ? 'Removing…' : 'Remove'}</Pressable>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

// ── Grouped Tab (Class / School / Exam) ──────────────────────────────────────
function GroupedTab({ scope }: { scope: 'class' | 'school' | 'exam' }) {
  const { data } = useGroupedLeaderboard(scope);
  const [expandedGroup, setExpandedGroup] = useState<string | null>(null);

  const groups = data?.groups ?? [];
  const labels = { class: 'Classes', school: 'Schools', exam: 'Exams' } as const;
  const singleLabel = { class: 'class_section', school: 'school', exam: 'exam' } as const;

  if (groups.length === 0) {
    return (
      <div className="bg-white border border-slate-200/80 rounded-2xl shadow-card text-center py-12 px-6">
        <div className="mx-auto w-12 h-12 rounded-full bg-teal-50 text-squishy-tealDark flex items-center justify-center">
          <TrophyIcon />
        </div>
        <p className="text-sm text-slate-500 mt-3">
          {scope === 'class' ? "You aren't enrolled in any classes yet."
            : scope === 'school' ? "No school found for your account."
            : "No exams found. Start studying an exam to see rankings."}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-baseline justify-between">
        <h2 className="font-display text-lg font-semibold text-squishy-navy tracking-tight">My {labels[scope]}</h2>
        <span className="text-[11px] font-medium uppercase tracking-wide text-slate-400">Last 30 days</span>
      </div>
      {groups.map((g) => (
        <GroupSection
          key={g.groupId}
          group={g}
          scopeType={singleLabel[scope]}
          onExpand={() => setExpandedGroup(g.groupId)}
        />
      ))}

      {/* Expanded overlay */}
      {expandedGroup && (
        <ExpandedLeaderboard
          groupId={expandedGroup}
          scopeType={singleLabel[scope]}
          groups={groups}
          onClose={() => setExpandedGroup(null)}
        />
      )}
    </div>
  );
}

function GroupSection({ group, scopeType, onExpand }: {
  group: { groupId: string; groupName: string; entries: LeaderboardEntry[] };
  scopeType: string;
  onExpand: () => void;
}) {
  void scopeType;
  return (
    <div className="bg-white border border-slate-200/80 rounded-2xl shadow-card p-4">
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-display text-sm font-semibold text-squishy-navy tracking-tight truncate">{group.groupName}</h3>
        <Pressable
          onClick={onExpand}
          className="shrink-0 inline-flex items-center gap-0.5 min-h-[44px] px-2 -mr-1 text-xs font-semibold text-squishy-tealDark"
        >
          View all
          <svg viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5">
            <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
          </svg>
        </Pressable>
      </div>
      {group.entries.length === 0 ? (
        <p className="text-xs text-slate-400 py-2">No activity in the last 30 days</p>
      ) : (
        <ol className="divide-y divide-slate-100">
          {group.entries.slice(0, 3).map((e) => (
            <LeaderboardRow key={e.userId} entry={e} />
          ))}
        </ol>
      )}
    </div>
  );
}

// ── Global Tab ───────────────────────────────────────────────────────────────
function GlobalTab() {
  const { data } = useLeaderboard('global');
  return (
    <div className="space-y-4">
      <div className="flex items-baseline justify-between">
        <h2 className="font-display text-lg font-semibold text-squishy-navy tracking-tight">Global</h2>
        <span className="text-[11px] font-medium uppercase tracking-wide text-slate-400">Last 30 days</span>
      </div>
      <Podium entries={data?.entries ?? []} />
      <LeaderboardList entries={data?.entries ?? []} loading={!data} />
      {data?.currentUserRank && (
        <p className="text-xs text-slate-500 mt-2 text-center">Your rank: #{data.currentUserRank} of {data.totalUsers}</p>
      )}
    </div>
  );
}

// ── Expanded Leaderboard Overlay ─────────────────────────────────────────────
function ExpandedLeaderboard({ groupId, scopeType, groups, onClose }: {
  groupId: string;
  scopeType: string;
  groups: { groupId: string; groupName: string; entries: LeaderboardEntry[] }[];
  onClose: () => void;
}) {
  const group = groups.find((g) => g.groupId === groupId);
  // Fetch full leaderboard for this scope
  const scopeMap: Record<string, string> = {
    'class_section': 'class_section',
    'school': 'school',
    'exam': 'exam',
  };
  const { data } = useLeaderboard(scopeMap[scopeType] as any, groupId);
  const entries = data?.entries ?? group?.entries ?? [];

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm animate-fade-in" onClick={onClose}>
      <div
        className="absolute inset-x-0 bottom-0 top-12 bg-white rounded-t-3xl overflow-hidden animate-slide-up flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="pt-2.5 flex justify-center" aria-hidden="true">
          <div className="w-10 h-1 rounded-full bg-slate-200" />
        </div>
        <div className="px-5 pt-2 pb-3 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h2 className="font-display text-lg font-semibold text-squishy-navy tracking-tight truncate">
              {group?.groupName ?? data?.scopeName ?? 'Rankings'}
            </h2>
            <p className="text-xs text-slate-500">Last 30 days</p>
          </div>
          <Pressable
            onClick={onClose}
            aria-label="Close"
            className="shrink-0 w-11 h-11 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-600 active:bg-slate-100 transition-colors"
          >
            <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
              <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
            </svg>
          </Pressable>
        </div>
        <div className="flex-1 overflow-y-auto px-5 pb-8 space-y-4">
          <Podium entries={entries} />
          <LeaderboardList entries={entries} loading={!data && !group?.entries?.length} />
          {data?.currentUserRank && (
            <p className="text-xs text-slate-500 text-center">Your rank: #{data.currentUserRank} of {data.totalUsers}</p>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Podium (top 3) ───────────────────────────────────────────────────────────

function Podium({ entries }: { entries: LeaderboardEntry[] }) {
  if (entries.length < 3) return null;
  const [first, second, third] = entries;
  return (
    <div className="relative bg-white border border-slate-200/80 rounded-2xl shadow-card px-4 pt-6 pb-0 overflow-hidden">
      <Confetti count={18} duration={1.8} />
      <div className="flex items-end justify-center gap-3">
        <PodiumSpot entry={second!} place={2} />
        <PodiumSpot entry={first!} place={1} />
        <PodiumSpot entry={third!} place={3} />
      </div>
    </div>
  );
}

function PodiumSpot({ entry: e, place }: { entry: LeaderboardEntry; place: 1 | 2 | 3 }) {
  const ring = place === 1 ? 'ring-squishy-gold' : place === 2 ? 'ring-slate-300' : 'ring-orange-300';
  const avatar = place === 1 ? 'w-16 h-16 text-xl' : 'w-12 h-12 text-base';
  const pedestal = place === 1 ? 'h-16 bg-amber-50' : place === 2 ? 'h-11 bg-slate-100' : 'h-8 bg-orange-50';
  const numColor = place === 1 ? 'text-amber-500' : place === 2 ? 'text-slate-400' : 'text-orange-400';
  const delay = place === 1 ? 0.28 : place === 2 ? 0.12 : 0.2;
  return (
    <motion.div
      className="flex flex-col items-center flex-1 max-w-[7rem] min-w-0"
      initial={{ opacity: 0, y: 24, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ ...springs.bouncy, delay }}
    >
      <div className="relative">
        {place === 1 && (
          <motion.span
            className="absolute -top-5 left-1/2 -translate-x-1/2 text-squishy-gold"
            initial={{ opacity: 0, y: 6, rotate: -12 }}
            animate={{ opacity: 1, y: 0, rotate: 0 }}
            transition={{ ...springs.bouncy, delay: delay + 0.2 }}
            aria-hidden="true"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
              <path d="M3 8l4.5 3L12 5l4.5 6L21 8l-1.5 9.5a1 1 0 01-1 .85h-13a1 1 0 01-1-.85L3 8z" />
            </svg>
          </motion.span>
        )}
        <div className={`${avatar} rounded-full ring-4 ${ring} bg-slate-200 overflow-hidden flex items-center justify-center font-semibold text-slate-600`}>
          {e.avatarUrl ? <img src={e.avatarUrl} alt="" className="w-full h-full object-cover" /> : e.displayName[0]}
        </div>
      </div>
      <div className="mt-2 max-w-full inline-flex items-center gap-0.5 text-xs font-semibold text-squishy-dark">
        <span className="truncate">{e.displayName}</span>
        {e.verifiedStudent && <VerifiedBadge size={12} />}
      </div>
      <div className="text-[11px] text-slate-500 tabular-nums">
        <CountUp value={e.xp} /> XP
      </div>
      <div className={`mt-2 w-full ${pedestal} rounded-t-xl flex items-start justify-center pt-1.5`}>
        <span className={`font-display text-lg font-bold ${numColor}`}>{place}</span>
      </div>
    </motion.div>
  );
}

// ── Shared Components ────────────────────────────────────────────────────────

function LeaderboardList({ entries, onRemove, loading }: {
  entries: LeaderboardEntry[];
  onRemove?: (e: LeaderboardEntry) => void;
  loading?: boolean;
}) {
  if (loading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-16" />
        <Skeleton className="h-16" />
        <Skeleton className="h-16" />
        <Skeleton className="h-16" />
      </div>
    );
  }
  if (entries.length === 0) {
    return (
      <div className="bg-white border border-slate-200/80 rounded-2xl shadow-card text-center py-10 px-6">
        <div className="mx-auto w-12 h-12 rounded-full bg-teal-50 text-squishy-tealDark flex items-center justify-center">
          <TrophyIcon />
        </div>
        <p className="text-sm text-slate-500 mt-3">No activity in the last 30 days</p>
      </div>
    );
  }
  return (
    <ol className="bg-white rounded-2xl border border-slate-200/80 shadow-card divide-y divide-slate-100 overflow-hidden">
      {entries.map((e) => (
        <LeaderboardRow key={e.userId} entry={e} onRemove={onRemove} />
      ))}
    </ol>
  );
}

function LeaderboardRow({ entry: e, onRemove }: { entry: LeaderboardEntry; onRemove?: (e: LeaderboardEntry) => void }) {
  const invite = useInviteFriend();
  const [showAdd, setShowAdd] = useState(false);
  // In the Friends tab (onRemove set) the row is already a friend: show a delete
  // button and suppress the add-friend popover.
  const removable = Boolean(onRemove) && !e.isCurrentUser;

  return (
    <li className={`flex items-center gap-3 px-3 py-2.5 ${e.isCurrentUser ? 'bg-teal-50' : ''}`}>
      <RankBadge rank={e.rank} />
      <div className="w-10 h-10 shrink-0 rounded-full bg-gradient-to-br from-slate-100 to-slate-200 overflow-hidden flex items-center justify-center text-xs font-semibold text-slate-600">
        {e.avatarUrl ? <img src={e.avatarUrl} alt="" className="w-full h-full object-cover" /> : e.displayName[0]}
      </div>
      <button
        className="flex-1 text-left font-medium text-sm text-squishy-dark hover:text-squishy-tealDark transition inline-flex items-center gap-1 min-w-0 min-h-[44px]"
        onClick={() => { if (!e.isCurrentUser && !onRemove) setShowAdd(!showAdd); }}
      >
        <span className="truncate">{e.displayName}</span>
        {e.verifiedStudent && <VerifiedBadge />}
        {e.isCurrentUser && (
          <span className="ml-1 shrink-0 text-[10px] font-bold uppercase tracking-wide text-squishy-tealDark bg-white rounded-full px-1.5 py-0.5 border border-squishy-teal/40">You</span>
        )}
      </button>
      <div className="shrink-0 text-[11px] font-semibold text-slate-500 bg-slate-100 rounded-full px-2 py-1">L{e.level}</div>
      <div className="shrink-0 text-sm font-semibold text-squishy-dark tabular-nums text-right min-w-[4.25rem]">
        <CountUp value={e.xp} />
        <span className="ml-0.5 text-[10px] font-medium text-slate-400">XP</span>
      </div>

      {/* Inline add-friend popover (not shown for existing friends) */}
      {showAdd && !e.isCurrentUser && !onRemove && (
        <Pressable
          haptic="success"
          onClick={async (ev) => {
            ev.stopPropagation();
            await invite.mutateAsync({ userId: e.userId });
            setShowAdd(false);
          }}
          disabled={invite.isPending}
          className="shrink-0 bg-squishy-primary text-white text-xs font-semibold px-3 min-h-[44px] rounded-full shadow-pop disabled:opacity-50"
        >
          {invite.isSuccess ? 'Sent!' : invite.isPending ? '...' : '+ Friend'}
        </Pressable>
      )}

      {/* Remove-friend button (Friends tab only) */}
      {removable && (
        <Pressable
          onClick={() => onRemove!(e)}
          aria-label={`Remove ${e.displayName}`}
          className="shrink-0 -mr-1 p-3 rounded-full text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
        >
          <TrashIcon />
        </Pressable>
      )}
    </li>
  );
}

function RankBadge({ rank }: { rank: number }) {
  const medal =
    rank === 1 ? 'bg-amber-50 text-amber-600' :
    rank === 2 ? 'bg-slate-100 text-slate-500' :
    rank === 3 ? 'bg-orange-50 text-orange-600' : '';
  if (medal) {
    return (
      <span className={`w-8 h-8 shrink-0 rounded-full flex items-center justify-center text-xs font-bold tabular-nums ${medal}`}>
        {rank}
      </span>
    );
  }
  return <span className="w-8 shrink-0 text-center text-sm font-semibold text-slate-400 tabular-nums">{rank}</span>;
}

function TrashIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
      <path fillRule="evenodd" d="M8.75 1A2.75 2.75 0 006 3.75v.443c-.795.077-1.58.177-2.365.298a.75.75 0 10.23 1.482l.149-.022.841 10.518A2.75 2.75 0 007.596 19h4.807a2.75 2.75 0 002.742-2.53l.841-10.52.149.023a.75.75 0 00.23-1.482A41.03 41.03 0 0014 4.193V3.75A2.75 2.75 0 0011.25 1h-2.5zM10 4c.84 0 1.673.025 2.5.075V3.75c0-.69-.56-1.25-1.25-1.25h-2.5c-.69 0-1.25.56-1.25 1.25v.325C8.327 4.025 9.16 4 10 4zM8.58 7.72a.75.75 0 00-1.5.06l.3 7.5a.75.75 0 101.5-.06l-.3-7.5zm4.34.06a.75.75 0 10-1.5-.06l-.3 7.5a.75.75 0 101.5.06l.3-7.5z" clipRule="evenodd" />
    </svg>
  );
}

function TrophyIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="w-6 h-6">
      <path fillRule="evenodd" d="M10 1c-1.828 0-3.623.149-5.371.435a.75.75 0 00-.629.74v.387c-.827.157-1.642.345-2.445.564a.75.75 0 00-.552.698 5 5 0 004.503 5.152 6.001 6.001 0 003.744 3.468v2.056H7.5a2.5 2.5 0 00-2.5 2.5.75.75 0 00.75.75h8.5a.75.75 0 00.75-.75 2.5 2.5 0 00-2.5-2.5h-1.75v-2.056a6.001 6.001 0 003.744-3.468 5 5 0 004.503-5.152.75.75 0 00-.552-.698 31.804 31.804 0 00-2.445-.564v-.386a.75.75 0 00-.629-.741A33.227 33.227 0 0010 1zM2.525 4.422A28.618 28.618 0 014 4.098v.752c0 .972.232 1.89.643 2.703a3.5 3.5 0 01-2.118-3.13zm14.95 0a3.5 3.5 0 01-2.118 3.131c.411-.814.643-1.731.643-2.703v-.752c.498.1.99.208 1.475.324z" clipRule="evenodd" />
    </svg>
  );
}

function UserAvatar({ name }: { name: string }) {
  return (
    <div className="w-10 h-10 shrink-0 rounded-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center text-sm font-semibold text-slate-600">
      {name[0]?.replace('@', '')}
    </div>
  );
}

function formatFriendName(f: any): string {
  const real = `${f.first_name} ${f.last_name?.[0] ?? ''}.`;
  // Accepted friends always see your real name, even if you're anonymous / use a handle.
  if (f.status === 'accepted') return real;
  if (f.display_name_mode === 'custom' && f.custom_handle) return `@${f.custom_handle}`;
  if (f.display_name_mode === 'anonymous') return `Student #${f.id.slice(0, 4).toUpperCase()}`;
  return real;
}
